<template>
  <v-dialog v-model="showDialog" max-width="980" persistent>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span>Add External Server</span>
        <v-spacer />
        <v-btn icon variant="text" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-text>
        <v-tabs v-model="activeTab" density="compact">
          <v-tab value="available">Available</v-tab>
          <v-tab value="manual">Manual URL</v-tab>
        </v-tabs>

        <v-window v-model="activeTab" class="mt-4">
          <v-window-item value="available">
            <v-text-field
              v-model="searchQuery"
              label="Search available external servers"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
            />
            <v-card
              v-for="server in availableServers"
              :key="server.name"
              class="mb-2 available-server-card"
              variant="outlined"
            >
              <v-card-text class="py-3 px-4">
                <div class="d-flex align-start ga-3">
                  <div class="flex-grow-1 min-w-0">
                    <div class="text-subtitle-1 mb-1">{{ server.displayName }}</div>
                    <div v-if="server.displayName !== server.name" class="text-caption text-medium-emphasis mb-1">
                      Key: {{ server.name }}
                    </div>
                    <div class="text-body-2 mb-1">{{ server.description }}</div>
                    <div v-if="server.url" class="text-caption mb-1 break-url">{{ server.url }}</div>
                    <div v-if="server.repositoryUrl" class="text-caption mb-1 break-url">{{ server.repositoryUrl }}</div>
                    <div v-if="server.licenseName" class="text-caption">{{ server.licenseName }}</div>
                  </div>
                  <v-btn color="primary" variant="outlined" class="available-server-card-action" @click="selectAvailableServer(server)">
                    Install
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
            <v-alert
              v-if="availableServers.length === 0 && allAvailableServers.length === 0"
              type="info"
              density="compact"
              variant="tonal"
            >
              No shared external servers are currently available to install.
            </v-alert>
            <v-alert
              v-else-if="availableServers.length === 0"
              type="info"
              density="compact"
              variant="tonal"
            >
              No available external servers match this search.
            </v-alert>
          </v-window-item>

          <v-window-item value="manual">
            <v-alert v-if="manualError" type="error" density="compact" class="mb-3">{{ manualError }}</v-alert>

            <v-text-field v-model="manual.displayName" label="Name" variant="outlined" class="mb-3 mt-2" />
            <v-textarea v-model="manual.description" label="Description" variant="outlined" class="mb-3" />
            <v-text-field
              v-model="manual.url"
              label="Remote MCP URL"
              hint="Must be an HTTPS streamable HTTP MCP endpoint."
              persistent-hint
              variant="outlined"
              class="mb-3"
            />
            <v-btn-toggle v-model="manual.authMode" mandatory divided class="mb-4">
              <v-btn value="none">No Auth</v-btn>
              <v-btn value="oauth2">OAuth 2.0</v-btn>
            </v-btn-toggle>

            <div class="text-subtitle-2 mb-2">Secrets</div>
            <div v-for="(field, index) in manual.secretFields" :key="`secret-field-${index}`" class="mb-3">
              <v-text-field v-model="field.name" label="Secret Name" variant="outlined" class="mb-2" />
              <v-text-field v-model="field.label" label="Label" variant="outlined" class="mb-2" />
              <v-text-field v-model="field.description" label="Description" variant="outlined" class="mb-2" />
              <v-checkbox v-model="field.required" label="Required" density="compact" />
            </div>
            <div class="manual-actions">
              <v-btn
                v-if="manual.secretFields.length < 5"
                variant="text"
                color="primary"
                @click="addSecretField"
              >
                Add Secret
              </v-btn>
            </div>

            <div v-if="manual.authMode === 'oauth2'" class="mt-4">
              <div class="d-flex align-center mb-3">
                <div class="text-subtitle-2">Authentication Discovery</div>
                <v-spacer />
                <v-btn color="primary" variant="outlined" :loading="discovering" @click="discoverAuthentication">
                  Discover Authentication
                </v-btn>
              </div>

              <v-alert type="warning" density="compact" variant="tonal" class="mb-3">
                MissionSquad discovers authorization metadata automatically in the normal flow. Manual endpoint entry is only available in the advanced section below.
              </v-alert>

              <v-alert v-if="discoveryError" type="error" density="compact" class="mb-3">
                <div>{{ discoveryError }}</div>
                <div v-if="discoveryErrorCause" class="text-caption mt-1">
                  Cause: {{ discoveryErrorCause }}
                </div>
                <div v-if="boundedDiscoveryAttemptedUrls.length > 0" class="text-caption mt-1">
                  Attempted:
                  <div v-for="attemptedUrl in boundedDiscoveryAttemptedUrls" :key="attemptedUrl">
                    {{ attemptedUrl }}
                  </div>
                </div>
              </v-alert>

              <v-card v-if="showIssuerOverrideFallback" variant="outlined" class="mb-3">
                <v-card-text>
                  <div class="text-subtitle-2 mb-2">Specify Authorization Server</div>
                  <div class="text-body-2 mb-3">
                    Use this only when the MCP server failed discovery but you know the provider's authorization server issuer URL.
                  </div>
                  <v-text-field
                    v-model="issuerOverrideInput"
                    label="Authorization Server Issuer"
                    hint="Enter the issuer URL. Do not enter the authorization endpoint."
                    persistent-hint
                    variant="outlined"
                    class="mb-3"
                  />
                  <v-btn color="primary" variant="outlined" :loading="discovering" @click="retryDiscoveryWithIssuer">
                    Retry Discovery With Issuer
                  </v-btn>
                </v-card-text>
              </v-card>

              <template v-if="oauthDiscovery">
                <v-select
                  v-if="oauthDiscovery.authorizationServers.length > 1"
                  v-model="selectedAuthorizationServerIssuer"
                  :items="authorizationServerOptions"
                  item-title="title"
                  item-value="value"
                  label="Authorization Server"
                  variant="outlined"
                  class="mb-3"
                />

                <v-card variant="outlined" class="mb-3">
                  <v-card-text>
                    <div class="text-subtitle-2 mb-2">Discovered Provider</div>
                    <div class="text-body-2 mb-2"><strong>Issuer:</strong> {{ selectedAuthorizationServer?.issuer }}</div>
                    <div class="text-body-2 mb-2"><strong>Authorization Endpoint:</strong> {{ selectedAuthorizationServer?.authorizationEndpoint }}</div>
                    <div class="text-body-2 mb-2"><strong>Token Endpoint:</strong> {{ selectedAuthorizationServer?.tokenEndpoint }}</div>
                    <div class="text-body-2 mb-2">
                      <strong>Default Scopes:</strong> {{ discoveredScopesLabel }}
                    </div>
                    <div v-if="oauthDiscovery.discoverySource === 'issuer_override'" class="text-body-2 mb-2">
                      <strong>Compatibility Mode:</strong> PRM was not discovered. OAuth resource URI will use the canonical MCP URL.
                    </div>
                    <div class="text-body-2">
                      <strong>Registration Mode:</strong> {{ registrationModeSummary }}
                    </div>
                  </v-card-text>
                </v-card>

                <v-checkbox
                  v-if="canUseAutomaticRegistration"
                  v-model="manual.useManualClientCredentials"
                  label="Use manual client ID / secret"
                  density="compact"
                  class="mb-3"
                />

                <v-select
                  v-if="availableRegistrationModeOptions.length > 0"
                  v-model="manual.selectedRegistrationMode"
                  :items="availableRegistrationModeOptions"
                  item-title="title"
                  item-value="value"
                  label="Registration Mode"
                  variant="outlined"
                  class="mb-3"
                />

                <div v-if="manualClientCredentialsEntryEnabled" class="mb-3">
                  <v-text-field
                    v-model="manual.oauthClientConfig.clientId"
                    label="Client ID"
                    variant="outlined"
                    class="mb-3"
                  />
                  <v-text-field
                    v-model="manual.oauthClientConfig.clientSecret"
                    label="Client Secret"
                    variant="outlined"
                    class="mb-3"
                  />
                  <v-text-field
                    v-model="manual.defaultScopes"
                    label="Scopes Override"
                    hint="Comma-separated. Leave blank to use the discovered default scope set."
                    persistent-hint
                    variant="outlined"
                  />
                </div>
              </template>

              <v-expansion-panels variant="accordion" class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-title>Advanced Manual OAuth Setup</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <v-alert type="warning" density="compact" variant="tonal" class="mb-3">
                      This bypasses discovery. Use it only when the provider metadata cannot be discovered automatically.
                    </v-alert>
                    <v-text-field
                      v-model="manual.manualOAuth.authorizationEndpoint"
                      label="Authorization Endpoint"
                      variant="outlined"
                      class="mb-3"
                    />
                    <v-text-field
                      v-model="manual.manualOAuth.tokenEndpoint"
                      label="Token Endpoint"
                      variant="outlined"
                      class="mb-3"
                    />
                    <v-text-field
                      v-model="manual.oauthClientConfig.clientId"
                      label="Client ID"
                      variant="outlined"
                      class="mb-3"
                    />
                    <v-text-field
                      v-model="manual.oauthClientConfig.clientSecret"
                      label="Client Secret"
                      variant="outlined"
                      class="mb-3"
                    />
                    <v-text-field
                      v-model="manual.defaultScopes"
                      label="Scopes Override"
                      hint="Comma-separated optional scopes."
                      persistent-hint
                      variant="outlined"
                    />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-card variant="outlined" class="mt-4">
                <v-card-text>
                  <div class="d-flex align-center mb-3">
                    <div class="text-subtitle-2">Authorization Request Parameters</div>
                    <v-spacer />
                    <v-btn variant="text" color="secondary" @click="applyGoogleAuthorizationRequestParams">
                      Use Google Defaults
                    </v-btn>
                    <v-btn variant="text" color="primary" @click="addAuthorizationRequestParam">
                      Add Parameter
                    </v-btn>
                  </div>
                  <div class="text-body-2 mb-3">
                    Use this for provider-specific authorization URL params such as Google's
                    <code>access_type=offline</code> or <code>prompt=consent</code>.
                  </div>
                  <div
                    v-for="(param, index) in manual.authorizationRequestParams"
                    :key="`authorization-request-param-${index}`"
                    class="d-flex align-center ga-3 mb-3"
                  >
                    <v-text-field
                      v-model="param.name"
                      label="Parameter Name"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-text-field
                      v-model="param.value"
                      label="Parameter Value"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-btn icon variant="text" color="error" @click="removeAuthorizationRequestParam(index)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                  <div
                    v-if="manual.authorizationRequestParams.length === 0"
                    class="text-caption text-medium-emphasis"
                  >
                    No provider-specific authorization params configured.
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <v-btn color="primary" class="manual-submit-button" @click="continueToInstall">Create & Install</v-btn>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {
  CatalogExternalMcpServer,
  CreateExternalMcpServerRequest,
  DiscoverExternalMcpOAuthResponse,
  DiscoveredAuthorizationServer,
  UserVisibleMcpServer
} from '@/types/mcp'
import { isApiError } from '@/types/api'
import { compareUserVisibleMcpServers, matchesUserVisibleMcpServer } from '@/utils/mcpServers'
import { Options, Vue } from 'vue-class-component'

function parseScopes(value: string): string[] {
  return value
    .split(',')
    .map((scope) => scope.trim())
    .filter(Boolean)
}

function deriveManualServerNameFromDisplayName(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function isAbsoluteHttpsUrl(value: string): boolean {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function normalizeComparableUrl(value: string): string {
  try {
    return new URL(value).toString()
  } catch {
    return value.trim()
  }
}

function canonicalizeExternalOAuthResourceUri(value: string): string {
  const parsed = new URL(value)
  parsed.search = ''
  parsed.hash = ''
  return parsed.toString()
}

function resolveDiscoveredExternalTransportUrl(
  inputUrl: string,
  discovery: DiscoverExternalMcpOAuthResponse | null
): string {
  const canonicalInputUrl = canonicalizeExternalOAuthResourceUri(inputUrl)
  if (!discovery?.resourceUri) {
    return canonicalInputUrl
  }

  const inputParsed = new URL(canonicalInputUrl)
  const discoveredResourceParsed = new URL(canonicalizeExternalOAuthResourceUri(discovery.resourceUri))
  const inputPath = inputParsed.pathname.replace(/\/+$/, '') || '/'
  const discoveredPath = discoveredResourceParsed.pathname.replace(/\/+$/, '') || '/'

  if (
    inputParsed.origin === discoveredResourceParsed.origin &&
    inputPath === '/' &&
    discoveredPath !== '/'
  ) {
    return discoveredResourceParsed.toString()
  }

  return canonicalInputUrl
}

const OAUTH_AUTHORIZATION_REQUEST_PARAM_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,99}$/
const RESERVED_OAUTH_AUTHORIZATION_REQUEST_PARAM_NAMES = new Set([
  'response_type',
  'client_id',
  'redirect_uri',
  'state',
  'scope',
  'resource',
  'code_challenge',
  'code_challenge_method'
])

function normalizeAuthorizationRequestParams(
  input: Array<{ name: string; value: string }>
): Array<{ name: string; value: string }> {
  return input.map((entry) => ({
    name: entry.name.trim(),
    value: entry.value.trim()
  }))
}

function validateAuthorizationRequestParams(
  input: Array<{ name: string; value: string }>
): string | null {
  const normalized = normalizeAuthorizationRequestParams(input)
  const seenNames = new Set<string>()

  for (const entry of normalized) {
    if (!entry.name) {
      return 'Authorization request parameter name is required.'
    }
    if (!OAUTH_AUTHORIZATION_REQUEST_PARAM_NAME_PATTERN.test(entry.name)) {
      return `Authorization request parameter name is invalid: ${entry.name}`
    }
    if (RESERVED_OAUTH_AUTHORIZATION_REQUEST_PARAM_NAMES.has(entry.name)) {
      return `Authorization request parameter ${entry.name} is reserved and cannot be overridden.`
    }
    if (!entry.value) {
      return `Authorization request parameter ${entry.name} requires a value.`
    }
    if (seenNames.has(entry.name)) {
      return `Authorization request parameter ${entry.name} is duplicated.`
    }
    seenNames.add(entry.name)
  }

  return null
}

const GOOGLE_OAUTH_AUTHORIZATION_REQUEST_PARAMS = [
  { name: 'access_type', value: 'offline' },
  { name: 'prompt', value: 'consent' },
  { name: 'include_granted_scopes', value: 'true' }
] as const

function normalizeExternalSharedUrlAndSecrets(
  inputUrl: string,
  existingFields: Array<{ name: string; label: string; description: string; required: boolean; inputType: 'password' }>
): {
  sanitizedUrl: string
  secretFields: Array<{ name: string; label: string; description: string; required: boolean; inputType: 'password' }>
  initialSecrets: Array<{ name: string; value: string }>
} {
  const parsed = new URL(inputUrl)
  const initialSecrets: Array<{ name: string; value: string }> = []
  const secretFields = [...existingFields]

  for (const [name, value] of parsed.searchParams.entries()) {
    if (!/^[A-Za-z_][A-Za-z0-9_]{0,49}$/.test(name)) {
      throw new Error(`Query parameter "${name}" cannot be converted into a secret field name.`)
    }
    initialSecrets.push({ name, value })
    if (!secretFields.some((field) => field.name === name)) {
      secretFields.push({
        name,
        label: name,
        description: `Extracted from URL query parameter "${name}"`,
        required: true,
        inputType: 'password'
      })
    }
  }

  parsed.search = ''
  parsed.hash = ''

  return {
    sanitizedUrl: parsed.toString(),
    secretFields,
    initialSecrets
  }
}

function getKnownProviderUrlError(value: string): string | null {
  try {
    const parsed = new URL(value)
    const pathname = parsed.pathname.replace(/\/+$/, '') || '/'
    if (parsed.origin === 'https://mcp.zapier.com' && pathname !== '/api/v1/connect') {
      return 'Zapier requires the Integration URL ending in /api/v1/connect. Do not use https://mcp.zapier.com/mcp.'
    }
    return null
  } catch {
    return null
  }
}

const INVALID_ISSUER_OVERRIDE_PATH_SUFFIXES = [
  '/authorize',
  '/authorization',
  '/oauth/token',
  '/token',
  '/openid-configuration',
  '/oauth-authorization-server'
]

function normalizeAuthorizationServerIssuerOverrideInput(value: string): string {
  const trimmed = value.trim()
  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    throw new Error('Authorization server issuer must be a valid absolute HTTPS URL.')
  }

  if (parsed.protocol !== 'https:') {
    throw new Error('Authorization server issuer must use HTTPS.')
  }
  if (parsed.username || parsed.password) {
    throw new Error('Authorization server issuer must not include embedded credentials.')
  }
  if (parsed.search) {
    throw new Error('Authorization server issuer must not include a query string.')
  }
  if (parsed.hash) {
    throw new Error('Authorization server issuer must not include a fragment.')
  }

  const normalizedPathname = parsed.pathname.replace(/\/+$/, '').toLowerCase() || '/'
  if (INVALID_ISSUER_OVERRIDE_PATH_SUFFIXES.some((suffix) => normalizedPathname.endsWith(suffix))) {
    throw new Error(
      'Authorization server issuer must be an issuer URL, not an authorization, token, or well-known metadata endpoint.'
    )
  }

  return trimmed
}

@Options({
  props: {
    show: Boolean,
    existingServers: Object
  },
  emits: ['close', 'review', 'installExisting'],
  watch: {
    show: {
      async handler(value: boolean) {
        if (value === true) {
          this.activeTab = 'available'
          this.searchQuery = ''
        }
      },
      immediate: false
    },
    'manual.displayName': {
      handler(value: string) {
        this.manual.name = deriveManualServerNameFromDisplayName(value)
      }
    },
    'manual.authMode': {
      handler(value: 'none' | 'oauth2') {
        if (value === 'none') {
          ;(this as any).resetOAuthState()
        }
      }
    },
    'manual.url': {
      handler() {
        ;(this as any).handleManualUrlChanged()
      }
    },
    issuerOverrideInput: {
      handler() {
        ;(this as any).handleIssuerOverrideChanged()
      }
    }
  }
})
export default class ExternalMcpServerCatalogDialog extends Vue {
  show!: boolean
  existingServers!: Record<string, UserVisibleMcpServer>
  activeTab = 'available'
  searchQuery = ''
  selectedCatalogServer: CatalogExternalMcpServer | null = null
  manualError = ''
  discovering = false
  discoveryError = ''
  discoveryErrorCause = ''
  discoveryAttemptedUrls: string[] = []
  oauthDiscovery: DiscoverExternalMcpOAuthResponse | null = null
  selectedAuthorizationServerIssuer = ''
  issuerOverrideInput = ''
  lastDiscoveryAttemptMode: 'prm' | 'issuer_override' | null = null
  lastPrmFailureComparableUrl = ''
  lastPrmFailureError = ''
  lastPrmFailureCause = ''
  lastPrmFailureAttemptedUrls: string[] = []
  manual = {
    name: '',
    displayName: '',
    description: '',
    url: '',
    authMode: 'none' as 'none' | 'oauth2',
    homepageUrl: '',
    repositoryUrl: '',
    licenseName: '',
    secretFields: [] as Array<{ name: string; label: string; description: string; required: boolean; inputType: 'password' }>,
    useManualClientCredentials: false,
    oauthClientConfig: {
      clientId: '',
      clientSecret: '',
      scopes: [] as string[]
    },
    selectedRegistrationMode: 'manual' as 'cimd' | 'dcr' | 'manual',
    authorizationRequestParams: [] as Array<{ name: string; value: string }>,
    defaultScopes: '',
    manualOAuth: {
      authorizationEndpoint: '',
      tokenEndpoint: ''
    }
  }

  get showDialog(): boolean {
    return this.show
  }

  get allAvailableServers(): UserVisibleMcpServer[] {
    return Object.values(this.existingServers || {})
      .filter((server) => server.source === 'external' && server.installed !== true)
      .sort(compareUserVisibleMcpServers)
  }

  get availableServers(): UserVisibleMcpServer[] {
    return this.allAvailableServers.filter((server) => matchesUserVisibleMcpServer(server, this.searchQuery))
  }

  get authorizationServerOptions(): Array<{ title: string; value: string }> {
    return (this.oauthDiscovery?.authorizationServers || []).map((server) => ({
      title: server.issuer,
      value: server.issuer
    }))
  }

  get selectedAuthorizationServer(): DiscoveredAuthorizationServer | null {
    if (!this.oauthDiscovery) {
      return null
    }
    return (
      this.oauthDiscovery.authorizationServers.find(
        (server) => server.issuer === this.selectedAuthorizationServerIssuer
      ) || null
    )
  }

  get boundedDiscoveryAttemptedUrls(): string[] {
    return this.discoveryAttemptedUrls.slice(0, 3)
  }

  get currentComparableDiscoveryUrl(): string {
    const trimmedUrl = this.manual.url.trim()
    if (!trimmedUrl) {
      return ''
    }

    try {
      return normalizeComparableUrl(normalizeExternalSharedUrlAndSecrets(trimmedUrl, this.manual.secretFields).sanitizedUrl)
    } catch {
      return normalizeComparableUrl(trimmedUrl)
    }
  }

  get showIssuerOverrideFallback(): boolean {
    return (
      this.manual.authMode === 'oauth2' &&
      !this.oauthDiscovery &&
      !this.discovering &&
      this.lastPrmFailureComparableUrl !== '' &&
      this.lastPrmFailureComparableUrl === this.currentComparableDiscoveryUrl
    )
  }

  get discoveredScopesLabel(): string {
    const scopes =
      this.oauthDiscovery?.challengedScopes && this.oauthDiscovery.challengedScopes.length > 0
        ? this.oauthDiscovery.challengedScopes
        : this.selectedAuthorizationServer?.scopesSupported || []
    return scopes.length > 0 ? scopes.join(', ') : 'Provider did not advertise default scopes'
  }

  get canUseAutomaticRegistration(): boolean {
    return !!(
      this.selectedAuthorizationServer?.clientIdMetadataDocumentSupported ||
      this.selectedAuthorizationServer?.registrationEndpoint
    )
  }

  get availableRegistrationModeOptions(): Array<{ title: string; value: 'cimd' | 'dcr' | 'manual' }> {
    const options: Array<{ title: string; value: 'cimd' | 'dcr' | 'manual' }> = []

    if (this.selectedAuthorizationServer?.registrationEndpoint) {
      options.push({
        title: 'Automatic client registration (DCR)',
        value: 'dcr'
      })
    }
    if (this.selectedAuthorizationServer?.clientIdMetadataDocumentSupported) {
      options.push({
        title: 'Automatic client identity (CIMD)',
        value: 'cimd'
      })
    }
    options.push({
      title: 'Manual client ID / secret',
      value: 'manual'
    })

    return options
  }

  get discoveredRegistrationMode(): 'cimd' | 'dcr' | 'manual' {
    const availableValues = this.availableRegistrationModeOptions.map((option) => option.value)
    if (availableValues.includes(this.manual.selectedRegistrationMode)) {
      return this.manual.selectedRegistrationMode
    }
    if (availableValues.includes('dcr')) {
      return 'dcr'
    }
    if (availableValues.includes('cimd')) {
      return 'cimd'
    }
    return 'manual'
  }

  get registrationModeSummary(): string {
    if (this.discoveredRegistrationMode === 'manual') {
      return 'Manual client ID / secret'
    }
    if (this.discoveredRegistrationMode === 'cimd' && this.manual.useManualClientCredentials) {
      return 'Automatic client identity with optional manual client ID / secret'
    }
    if (this.discoveredRegistrationMode === 'dcr' && this.manual.useManualClientCredentials) {
      return 'Automatic client registration with optional manual client ID / secret'
    }
    if (this.discoveredRegistrationMode === 'cimd') {
      return 'Automatic client identity (CIMD available)'
    }
    if (this.discoveredRegistrationMode === 'dcr') {
      return 'Automatic client registration'
    }
    if (this.selectedAuthorizationServer?.registrationEndpoint) {
      return 'Manual client ID / secret (automatic registration available but disabled)'
    }
    return 'Manual client ID / secret required'
  }

  get manualClientCredentialsEntryEnabled(): boolean {
    return this.manual.useManualClientCredentials || this.discoveredRegistrationMode === 'manual'
  }

  async searchCatalog(): Promise<void> {
    await this.$store.dispatch('searchAvailableServers', this.searchQuery)
  }

  resetOAuthState(): void {
    this.discovering = false
    this.clearDiscoveryResult()
    this.clearDiscoveryError()
    this.issuerOverrideInput = ''
    this.lastDiscoveryAttemptMode = null
    this.lastPrmFailureComparableUrl = ''
    this.lastPrmFailureError = ''
    this.lastPrmFailureCause = ''
    this.lastPrmFailureAttemptedUrls = []
    this.manual.useManualClientCredentials = false
    this.manual.selectedRegistrationMode = 'manual'
    this.manual.oauthClientConfig.clientId = ''
    this.manual.oauthClientConfig.clientSecret = ''
    this.manual.authorizationRequestParams = []
    this.manual.defaultScopes = ''
    this.manual.manualOAuth.authorizationEndpoint = ''
    this.manual.manualOAuth.tokenEndpoint = ''
  }

  async discoverAuthentication(): Promise<void> {
    await this.performDiscovery('prm')
  }

  async retryDiscoveryWithIssuer(): Promise<void> {
    await this.performDiscovery('issuer_override')
  }

  private clearDiscoveryResult(): void {
    this.oauthDiscovery = null
    this.selectedAuthorizationServerIssuer = ''
  }

  private clearDiscoveryError(): void {
    this.discoveryError = ''
    this.discoveryErrorCause = ''
    this.discoveryAttemptedUrls = []
  }

  private recordPrmFailure(comparableUrl: string): void {
    this.lastPrmFailureComparableUrl = comparableUrl
    this.lastPrmFailureError = this.discoveryError
    this.lastPrmFailureCause = this.discoveryErrorCause
    this.lastPrmFailureAttemptedUrls = [...this.discoveryAttemptedUrls]
  }

  private restorePrmFailureIfAvailable(): void {
    if (!this.lastPrmFailureError || this.lastPrmFailureComparableUrl !== this.currentComparableDiscoveryUrl) {
      this.clearDiscoveryError()
      return
    }

    this.discoveryError = this.lastPrmFailureError
    this.discoveryErrorCause = this.lastPrmFailureCause
    this.discoveryAttemptedUrls = [...this.lastPrmFailureAttemptedUrls]
  }

  private handleManualUrlChanged(): void {
    this.clearDiscoveryResult()
    this.clearDiscoveryError()
    this.issuerOverrideInput = ''
    this.lastDiscoveryAttemptMode = null
    this.lastPrmFailureComparableUrl = ''
    this.lastPrmFailureError = ''
    this.lastPrmFailureCause = ''
    this.lastPrmFailureAttemptedUrls = []
    this.manual.useManualClientCredentials = false
  }

  private handleIssuerOverrideChanged(): void {
    if (this.lastDiscoveryAttemptMode === 'issuer_override' || this.oauthDiscovery?.discoverySource === 'issuer_override') {
      this.clearDiscoveryResult()
      this.restorePrmFailureIfAvailable()
    }
  }

  private applyDiscoveryError(error: unknown, mode: 'prm' | 'issuer_override', comparableUrl: string): void {
    if (isApiError(error)) {
      const data = error.data as { cause?: unknown; attemptedUrls?: unknown } | undefined
      const rawAttemptedUrls = data?.attemptedUrls
      const attemptedUrls = Array.isArray(rawAttemptedUrls)
        ? rawAttemptedUrls.filter((item): item is string => typeof item === 'string')
        : []
      this.discoveryError = error.message || 'Failed to discover OAuth metadata for this MCP server.'
      this.discoveryErrorCause = typeof data?.cause === 'string' ? data.cause : ''
      this.discoveryAttemptedUrls = attemptedUrls.slice(0, 3)
    } else {
      this.discoveryError = error instanceof Error ? error.message : 'Failed to discover OAuth metadata for this MCP server.'
      this.discoveryErrorCause = ''
      this.discoveryAttemptedUrls = []
    }

    if (mode === 'prm') {
      this.recordPrmFailure(comparableUrl)
    }
  }

  private async performDiscovery(mode: 'prm' | 'issuer_override'): Promise<void> {
    const url = this.manual.url.trim()
    if (!url) {
      this.discoveryError = 'Remote MCP URL is required before discovery.'
      return
    }
    const knownProviderUrlError = getKnownProviderUrlError(url)
    if (knownProviderUrlError) {
      this.discoveryError = knownProviderUrlError
      return
    }

    let comparableUrl = normalizeComparableUrl(url)
    let issuerOverride: string | undefined
    try {
      const normalized = normalizeExternalSharedUrlAndSecrets(url, this.manual.secretFields)
      comparableUrl = normalizeComparableUrl(normalized.sanitizedUrl)
      if (mode === 'issuer_override') {
        issuerOverride = normalizeAuthorizationServerIssuerOverrideInput(this.issuerOverrideInput)
      }
    } catch (error) {
      this.discoveryError = error instanceof Error ? error.message : 'Invalid URL query secret configuration.'
      return
    }

    this.discovering = true
    this.lastDiscoveryAttemptMode = mode
    this.clearDiscoveryResult()
    this.clearDiscoveryError()

    try {
      const { sanitizedUrl } = normalizeExternalSharedUrlAndSecrets(url, this.manual.secretFields)
      const discovery = (await this.$store.dispatch('discoverExternalServerOAuth', {
        url: sanitizedUrl,
        ...(issuerOverride ? { authorizationServerIssuerOverride: issuerOverride } : {})
      })) as DiscoverExternalMcpOAuthResponse
      this.oauthDiscovery = discovery
      this.selectedAuthorizationServerIssuer = discovery.authorizationServers[0]?.issuer || ''
      const availableRegistrationModes: Array<'cimd' | 'dcr' | 'manual'> = []
      if (discovery.authorizationServers.some((server) => typeof server.registrationEndpoint === 'string')) {
        availableRegistrationModes.push('dcr')
      }
      if (discovery.authorizationServers.some((server) => server.clientIdMetadataDocumentSupported === true)) {
        availableRegistrationModes.push('cimd')
      }
      availableRegistrationModes.push('manual')

      this.manual.selectedRegistrationMode =
        availableRegistrationModes.includes(this.manual.selectedRegistrationMode)
          ? this.manual.selectedRegistrationMode
          : availableRegistrationModes.includes('dcr')
            ? 'dcr'
            : discovery.recommendedRegistrationMode
      this.manual.useManualClientCredentials = this.manual.selectedRegistrationMode === 'manual'
      if (mode === 'prm') {
        this.lastPrmFailureComparableUrl = ''
        this.lastPrmFailureError = ''
        this.lastPrmFailureCause = ''
        this.lastPrmFailureAttemptedUrls = []
      }
    } catch (error) {
      this.applyDiscoveryError(error, mode, comparableUrl)
    } finally {
      this.discovering = false
    }
  }

  selectAvailableServer(server: UserVisibleMcpServer): void {
    this.$emit('installExisting', server.name)
  }

  addSecretField(): void {
    this.manual.secretFields.push({
      name: '',
      label: '',
      description: '',
      required: false,
      inputType: 'password'
    })
  }

  addAuthorizationRequestParam(): void {
    this.manual.authorizationRequestParams.push({
      name: '',
      value: ''
    })
  }

  removeAuthorizationRequestParam(index: number): void {
    this.manual.authorizationRequestParams.splice(index, 1)
  }

  applyGoogleAuthorizationRequestParams(): void {
    const nextParams = [...this.manual.authorizationRequestParams]

    for (const googleParam of GOOGLE_OAUTH_AUTHORIZATION_REQUEST_PARAMS) {
      const existingIndex = nextParams.findIndex((entry) => entry.name.trim() === googleParam.name)
      if (existingIndex >= 0) {
        nextParams.splice(existingIndex, 1, { ...googleParam })
      } else {
        nextParams.push({ ...googleParam })
      }
    }

    this.manual.authorizationRequestParams = nextParams
  }

  private buildDiscoveryOauthTemplate() {
    if (!this.oauthDiscovery || !this.selectedAuthorizationServer) {
      return null
    }

    return {
      authorizationServerIssuer: this.selectedAuthorizationServer.issuer,
      authorizationServerMetadataUrl: this.selectedAuthorizationServer.authorizationServerMetadataUrl,
      resourceMetadataUrl: this.oauthDiscovery.resourceMetadataUrl,
      resourceUri: this.oauthDiscovery.resourceUri,
      authorizationEndpoint: this.selectedAuthorizationServer.authorizationEndpoint,
      tokenEndpoint: this.selectedAuthorizationServer.tokenEndpoint,
      scopesSupported: this.selectedAuthorizationServer.scopesSupported,
      challengedScopes: this.oauthDiscovery.challengedScopes,
      codeChallengeMethodsSupported: this.selectedAuthorizationServer.codeChallengeMethodsSupported,
      pkceRequired: true,
      discoveryMode: 'auto' as const,
      discoverySource: this.oauthDiscovery.discoverySource,
      registrationMode: this.discoveredRegistrationMode,
      manualClientCredentialsAllowed: this.canUseAutomaticRegistration ? this.manual.useManualClientCredentials : undefined,
      clientIdMetadataDocumentSupported: this.selectedAuthorizationServer.clientIdMetadataDocumentSupported,
      registrationEndpoint: this.selectedAuthorizationServer.registrationEndpoint,
      tokenEndpointAuthMethodsSupported: this.selectedAuthorizationServer.tokenEndpointAuthMethodsSupported,
      authorizationRequestParams:
        this.manual.authorizationRequestParams.length > 0
          ? normalizeAuthorizationRequestParams(this.manual.authorizationRequestParams)
          : undefined
    }
  }

  private buildManualOauthTemplate() {
    return {
      authorizationServerIssuer: '',
      authorizationServerMetadataUrl: '',
      resourceMetadataUrl: '',
      resourceUri: canonicalizeExternalOAuthResourceUri(this.manual.url.trim()),
      authorizationEndpoint: this.manual.manualOAuth.authorizationEndpoint.trim(),
      tokenEndpoint: this.manual.manualOAuth.tokenEndpoint.trim(),
      scopesSupported: undefined,
      challengedScopes: parseScopes(this.manual.defaultScopes),
      codeChallengeMethodsSupported: ['S256'],
      pkceRequired: true,
      discoveryMode: 'manual' as const,
      registrationMode: 'manual' as const,
      clientIdMetadataDocumentSupported: undefined,
      registrationEndpoint: undefined,
      tokenEndpointAuthMethodsSupported: undefined,
      authorizationRequestParams:
        this.manual.authorizationRequestParams.length > 0
          ? normalizeAuthorizationRequestParams(this.manual.authorizationRequestParams)
          : undefined
    }
  }

  continueToInstall(): void {
    this.manualError = ''
    this.manual.name = deriveManualServerNameFromDisplayName(this.manual.displayName)

    if (!this.manual.displayName || !this.manual.url) {
      this.manualError = 'Name and remote MCP URL are required.'
      return
    }

    if (!this.manual.name) {
      this.manualError = 'Name must include at least one letter or number.'
      return
    }

    const trimmedUrl = this.manual.url.trim()
    const knownProviderUrlError = getKnownProviderUrlError(trimmedUrl)
    if (knownProviderUrlError) {
      this.manualError = knownProviderUrlError
      return
    }
    const authorizationRequestParamsError = validateAuthorizationRequestParams(
      this.manual.authorizationRequestParams
    )
    if (authorizationRequestParamsError) {
      this.manualError = authorizationRequestParamsError
      return
    }
    let normalizedUrlAndSecrets
    try {
      normalizedUrlAndSecrets = normalizeExternalSharedUrlAndSecrets(trimmedUrl, this.manual.secretFields)
    } catch (error) {
      this.manualError = error instanceof Error ? error.message : 'Invalid URL query secret configuration.'
      return
    }
    let sanitizedUrl = normalizedUrlAndSecrets.sanitizedUrl
    const usesAdvancedManualOauth =
      this.manual.authMode === 'oauth2' &&
      (this.manual.manualOAuth.authorizationEndpoint.trim() !== '' ||
        this.manual.manualOAuth.tokenEndpoint.trim() !== '')

    let oauthTemplate: CreateExternalMcpServerRequest['oauthTemplate']
    let oauthClientConfig: CreateExternalMcpServerRequest['oauthClientConfig']

    if (this.manual.authMode === 'oauth2') {
      if (usesAdvancedManualOauth) {
        if (
          !isAbsoluteHttpsUrl(this.manual.manualOAuth.authorizationEndpoint.trim()) ||
          !isAbsoluteHttpsUrl(this.manual.manualOAuth.tokenEndpoint.trim())
        ) {
          this.manualError = 'Advanced manual OAuth setup requires absolute HTTPS authorization and token endpoints.'
          return
        }
        if (!this.manual.oauthClientConfig.clientId.trim()) {
          this.manualError = 'Client ID is required for advanced manual OAuth setup.'
          return
        }
        oauthTemplate = this.buildManualOauthTemplate()
        oauthClientConfig = {
          clientId: this.manual.oauthClientConfig.clientId.trim(),
          clientSecret: this.manual.oauthClientConfig.clientSecret.trim() || undefined,
          scopes: parseScopes(this.manual.defaultScopes)
        }
      } else {
        if (!this.oauthDiscovery || normalizeComparableUrl(this.oauthDiscovery.serverUrl) !== normalizeComparableUrl(sanitizedUrl)) {
          this.manualError = 'Discover authentication before creating an OAuth external MCP server.'
          return
        }
        if (!this.selectedAuthorizationServer) {
          this.manualError = 'Choose an authorization server before creating this shared definition.'
          return
        }
        const discoveredOauthTemplate = this.buildDiscoveryOauthTemplate()
        if (!discoveredOauthTemplate) {
          this.manualError = 'Discover authentication before creating an OAuth external MCP server.'
          return
        }
        sanitizedUrl = resolveDiscoveredExternalTransportUrl(sanitizedUrl, this.oauthDiscovery)
        oauthTemplate = discoveredOauthTemplate
        if (this.manual.useManualClientCredentials || this.discoveredRegistrationMode === 'manual') {
          if (!this.manual.oauthClientConfig.clientId.trim()) {
            this.manualError = 'Client ID is required when manual client registration is selected.'
            return
          }
          oauthClientConfig = {
            clientId: this.manual.oauthClientConfig.clientId.trim(),
            clientSecret: this.manual.oauthClientConfig.clientSecret.trim() || undefined,
            scopes: parseScopes(this.manual.defaultScopes)
          }
        }
      }
    }

    const draft: CreateExternalMcpServerRequest = {
      name: this.manual.name.trim(),
      displayName: this.manual.displayName.trim(),
      description: this.manual.description.trim(),
      url: sanitizedUrl,
      authMode: this.manual.authMode,
      oauthTemplate,
      secretFields: normalizedUrlAndSecrets.secretFields.map((field) => ({
        ...field,
        name: field.name.trim(),
        label: field.label.trim(),
        description: field.description.trim()
      })),
      homepageUrl: this.manual.homepageUrl.trim() || undefined,
      repositoryUrl: this.manual.repositoryUrl.trim() || undefined,
      licenseName: this.manual.licenseName.trim() || undefined,
      oauthClientConfig,
      catalogProvider: 'manual',
      enabled: true
    }

    this.$emit('review', {
      draft,
      existingServer: this.existingServers?.[draft.name] || null,
      initialSecrets: normalizedUrlAndSecrets.initialSecrets
    })
  }

  close(): void {
    this.$emit('close')
  }
}
</script>

<style scoped>
.break-url {
  overflow-wrap: anywhere;
}

.available-server-card-action {
  flex-shrink: 0;
}

.manual-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 16px;
}

.manual-submit-button {
  margin-top: 24px;
}
</style>
